import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BBWTMessageService, LoggerService, UserService } from '../../services';
import { TaggedLogger } from '../../services/taggedLogger';
import { AuthenticationProvider } from './authenticationProvider';
import { IUser } from '../../interfaces/user';
import { sha512 } from 'src/app/utils/hash';
import { AuthResultEnum } from '../../enums/auth-result.enum';
import { NgModel } from '@angular/forms';
import { OrganisationTypeEnum } from '../../enums/organisation-types';
import { SaasService } from '../../utils/saas.service';

interface Enabling2FADTO {
  code: string;
  sharedKey: string;
  authenticatorUri: string;
  recoveryCode: string[];
}

interface AuthDTO {
  user: IUser;
  isSuccess: boolean;
  reasonPhrase: string;
  authenticatorEnabled: boolean;
  enabling2FA: Enabling2FADTO;
}

/**
 * Login Component /account/login
 *
 * Handles login
 */
@Component({
  selector: 'login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  user = {
    email: '',
    password: '',
    rememberMe: false,
    twoFactorCode: '',
    enabling2FA: null,
    useRecoveryCodeLogin: false,
    rememberDevice: false,
  };
  antiAutocomplete = true;
  formInit = false;
  captchaResponse = '';
  siteKey = '';
  chosenAuthProvider: AuthenticationProvider = AuthenticationProvider.NotRequired;
  enabling2FA: Enabling2FADTO = {
    code: null,
    sharedKey: null,
    authenticatorUri: null,
    recoveryCode: null,
  };
  credentialStep = true;
  authenticatorStep = false;
  enablingAuthenticatorStep = 0;
  Max2FASteps = 4;

  twoFaCodeInputElId = '2fa';
  private forwardUrl = '/app';
  private logger: TaggedLogger;
  public isCapsLockOn: boolean;

  errorMsg: string = null;

  constructor(
    public messageService: BBWTMessageService,
    private http: HttpClient,
    private router: Router,
    private elRef: ElementRef,
    private userService: UserService,
    private route: ActivatedRoute,
    loggerService: LoggerService,
    private saasService: SaasService,
  ) {
    this.logger = loggerService.taggedLogger('LoginComponent');

    let that = this;
    document.addEventListener('keydown', function (event) {
      that.isCapsLockOn = event?.getModifierState('CapsLock') || false;
    });
  }

  //DB - Angular14 @ViewChild(ReCaptchaComponent) captcha: ReCaptchaComponent;

  private _showPassword = false;

  get showPassword(): boolean {
    return this._showPassword;
  }

  set showPassword(value: boolean) {
    this._showPassword = value;
  }

  async ngOnInit() {
    // Temp auto logout fix
    if (!window.localStorage.getItem('local-storage-cleared')) {
      window.localStorage.clear();
      window.localStorage.setItem('local-storage-cleared', 'true');
    }

    this.logger.trace('ngOnInit()');

    this.route.queryParams.subscribe((params) => {
      if (params && params.logout) {
        this.logger.debug('User got here from .logout(), showing successfully logged out message');
        this.messageService.ShowSuccess('Logged Out', 'You have successfully logged out');
      }

      if (params && params.redirectTo) {
        this.logger.debug('User got here from a link, redirecting them to ', params.redirectTo);
        this.forwardUrl = params.redirectTo;
      }

      if (params && params.resetPassword) {
        this.logger.debug('User got here from reset password page, showing message');
        this.messageService.ShowSuccess(
          'Success.',
          'Please check your email for instructions to reset your password. For further assistance please contact operations@engineius.co.uk.',
        );
      }
    });

    await this.redirectIfUserIsLoggedIn();
    // this.getSiteKey();
    this.getAutoCompleteSettings();
  }

  /*
  getSiteKey() {
    this.logger.trace('getSiteKey()');
    this.http.get('/api/settings/getsitekey/', {responseType: 'text'})
      .toPromise()
      .then((site_key) => {
        if (site_key != null) {
          this.logger.debug('Got Site Key: ', site_key);
          this.siteKey = site_key;
        }
      })
      .catch((error) => {
        this.logger.error('Error getting site key:', error);
      });
  }
   */

  // todo: Should be moved to settings service (not implemented yet) #settings_server
  getAutoCompleteSettings() {
    this.logger.trace('getAutoCompleteSettings()');
    this.http
      .get('/api/settings/getautocomplete/', { responseType: 'text' })
      .toPromise()
      .then((autocomplete) => {
        this.logger.debug('Got Auto Complete settings: ', autocomplete);
        this.antiAutocomplete = autocomplete !== 'true';
        this.formInit = true;
      })
      .catch((error) => {
        this.logger.error('Error getting autocomplete settings:', error);
      });
  }

  submitReCaptcha(form) {
    if (!form.invalid) {
      this.logIn();
    }
  }

  // CAPTCHA TOKEN
  token: string | undefined;
  useCaptcha = false;

  // 2FA
  isAuthenticatorSelected(): boolean {
    return this.chosenAuthProvider === AuthenticationProvider.Authenticator;
  }

  /** Switches the challenge between authenticator code and single-use recovery code. */
  toggleRecoveryCodeLogin(): void {
    this.user.useRecoveryCodeLogin = !this.user.useRecoveryCodeLogin;
    this.user.twoFactorCode = '';
    this.errorMsg = null;

    // A recovery code sign-in cannot remember the device, so don't leave it set.
    if (this.user.useRecoveryCodeLogin) {
      this.user.rememberDevice = false;
    }

    this.focusTwoFaCodeInput();
  }

  verifyCode(): void {
    if (!this.enabling2FA || this.enabling2FA.code === '') return;

    this.errorMsg = null;

    var crpPassword = sha512(this.user.password);
    //var crpPassword = this.user.password
    var user = {
      Email: this.user.email,
      Password: crpPassword,
      RememberMe: this.user.rememberMe,
      captchaResponse: this.captchaResponse,
      twoFactorCode: this.user.twoFactorCode,
      enabling2FA: this.enabling2FA,
    };

    this.http
      .post<string[]>('api/account/enable-2fa', user)
      .toPromise()
      .then((result) => {
        this.enabling2FA.recoveryCode = result;
        this.authNext();
      })
      .catch((result) => {
        this.errorMsg = result.error.reasonPhrase;
        //this.messageService.ShowError('User Login', result.error.reasonPhrase);
      });
  }

  goToPortal() {
    return this.router.navigate([this.forwardUrl]);
  }

  private logIn() {
    this.logger.debug('login()');

    this.errorMsg = null;

    var crpPassword = sha512(this.user.password);
    //var crpPassword = this.user.password
    var user = {
      Email: this.user.email,
      Password: crpPassword,
      RememberMe: this.user.rememberMe,
      captchaResponse: this.captchaResponse,
      twoFactorCode: this.user.twoFactorCode,
      useRecoveryCodeLogin: this.user.useRecoveryCodeLogin,
      rememberDevice: this.user.rememberDevice,
    };

    this.http
      .post<AuthDTO>('api/account/login', user)
      .toPromise()
      .then((loginResult) => {
        this.logger.debug('Got Login Result', loginResult);

        if (loginResult.isSuccess) {
          if (this.saasService.redirectToSaasIfSaasUser(loginResult.user.organisationType)) {
            return;
          }
          this.logger.info('Logged in redirecting to ', this.forwardUrl);
          return this.goToPortal();
        }

        if (loginResult.authenticatorEnabled) {
          this.logger.debug('Authenticator Enabled');
          this.credentialStep = false;
          this.authenticatorStep = true;
          this.enablingAuthenticatorStep = 0;

          this.chosenAuthProvider = AuthenticationProvider.Authenticator;
          this.focusTwoFaCodeInput();
          return;
        }

        if (loginResult.enabling2FA) {
          this.logger.debug('Enabling 2FA');
          this.enabling2FA = loginResult.enabling2FA;

          this.credentialStep = false;
          this.authenticatorStep = false;
          this.enablingAuthenticatorStep = 1;
        }
      })
      .catch((result) => {
        this.logger.error('Error during login', result);
        if (result.error.authResultEnum == AuthResultEnum.Deactivated) {
          this.errorMsg = result.error.reasonPhrase;
          //this.messageService.ShowError('User Login', result.error.reasonPhrase, 3, 'deactivated');
        } else if (result.error.authResultEnum == AuthResultEnum.LockedOut) {
          this.errorMsg = result.error.reasonPhrase;
        } else {
          if (!this.authenticatorStep) {
            this.errorMsg = 'Invalid email and/or password.';
          } else {
            this.errorMsg = result.error.reasonPhrase;
          }
          //this.messageService.ShowError('User Login', result.error.reasonPhrase);
        }

        this.captchaResponse = '';
      });
  }

  /**
   * todo: double check that this is okay
   * todo: add UI to handle the redirect well
   * @private
   */
  private async redirectIfUserIsLoggedIn() {
    let user = null;
    try {
      user = await this.userService.getLoggedUser();
    } catch (e) {
      this.logger.warn('Could not get currently logged in user, user is likely not logged in');
    }

    if (user !== null) {
      this.logger.info('User is logged in, redirecting forward');

      if (this.saasService.redirectToSaasIfSaasUser(user.organisationType)) {
        return;
      }

      return this.goToPortal();
    }

    return null;
  }

  private focusTwoFaCodeInput() {
    setTimeout(() => {
      var twoFaCodeInputEl = document.getElementById(this.twoFaCodeInputElId);
      if (twoFaCodeInputEl) {
        twoFaCodeInputEl.focus();
      }
    }, 200);
  }

  clearEmail(emailModel: NgModel) {
    this.user.email = '';
    emailModel.control.markAsUntouched();
    emailModel.control.updateValueAndValidity();
  }

  forgotPassword() {
    const _ = this.router.navigate(['/account/forgotpassword']);
  }

  authBack() {
    if (this.enablingAuthenticatorStep > 0) {
      this.enablingAuthenticatorStep -= 1;
    }
  }

  authNext() {
    if (this.enablingAuthenticatorStep < this.Max2FASteps) {
      this.enablingAuthenticatorStep += 1;
    }
  }

  copyCodes() {
    const codesToCopy = this.enabling2FA.recoveryCode.join('\n');
    navigator.clipboard
      .writeText(codesToCopy)
      .then(() => {
        this.messageService.ShowSuccess('Copied', 'Recovery codes copied to clipboard');
      })
      .catch((err) => {
        this.messageService.ShowError('Error', 'Failed to copy recovery codes');
      });
  }
}
